import { useMemo, useCallback } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { message, Select, TableColumnsType } from 'antd';

import { Button } from '../../../../components';
import TitleBar from '../../../../components/TitleBar';
import { Footer } from '../../../../components/common/Footer';
import FinancialData from '../../../../components/common/FinancialData';
import QueryErrorBoundary from '../../../../components/error-handling';
import TableV3 from '../../../../components/generic/Table/TableV3';

import useBCPMetaData from '../../../../hooks/useBCPMetaData';
import { useUserContext } from '../../../../context/PermissionsContext';

import { FETCH_PROCESSES_AREAS_WITH_STRATEGY_BY_LOCATION } from '../../../dataInputs/queries/processAreas';
import { UPDATE_STRATEGY } from '../../queries';

import { getStatusEmoji, tableSortingMethods } from '../../../../utils';

import { ProcessArea, Strategy } from '../types';

import { baseColumns } from './exportColumns';

import './bcpprocessAreas.css';
import TitleUpdater from '../../../../components/common/TitleUpdater';

type FetchedData = {
  findAll: ProcessArea[];
};

const BCPProcessAreas = () => {
  const params = useParams();
  const { locationId } = params;

  const {
    locationName,
    loading: metaDataLoading,
    error: metaDataError,
  } = useBCPMetaData(locationId!);

  const backPath = `/bcp-landing/location/${locationId}`;
  const navigate = useNavigate();

  const { canViewFinancials } = useUserContext();

  const { data, loading, error } = useQuery<FetchedData>(
    FETCH_PROCESSES_AREAS_WITH_STRATEGY_BY_LOCATION,
    {
      variables: { uuid: locationId },
    },
  );

  const [UpdateStrategy, { loading: loadingUpdateStrategy }] = useMutation(
    UPDATE_STRATEGY,
    {
      refetchQueries: ['FindAllProcessAreasByLocation'],
    },
  );

  const tableData = data?.findAll || [];

  const loadingStates = metaDataLoading || loading || loadingUpdateStrategy;

  const onChangeStrategyRequired = useCallback(
    async (value: string, uuid: string) => {
      try {
        const response = await UpdateStrategy({
          variables: {
            data: {
              uuid: uuid,
              required: value === 'Yes',
            },
          },
        });
        if (response?.data) {
          message.open({
            type: 'success',
            content: 'Changes Saved',
          });
        }
      } catch (e) {
        message.open({
          type: 'error',
          content: 'Operation Failed',
        });
      }
    },
    [UpdateStrategy],
  );

  const renderStrategyRequired = (strategy: Strategy) => (
    <Select
      defaultValue={'Yes'}
      options={[
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
      ]}
      value={strategy?.required ? 'Yes' : 'No'}
      onChange={(value) => onChangeStrategyRequired(value, strategy?.uuid)}
    />
  );

  const customColumns: TableColumnsType<ProcessArea> = useMemo(
    () => [
      {
        key: 'name',
        title: 'Process Area/Process',
        width: 150,
        align: 'left',
        sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
        render: ({ name }) => (
          <span className="custom-table--column custom-table--column-primary">
            {name}
          </span>
        ),
      },
      {
        key: 'replacementTime',
        title: () => (
          <div>
            Replacement Time <br />
            (months)
          </div>
        ),
        width: 100,
        align: 'right',
        sorter: (a, b) =>
          tableSortingMethods.number(a.replacementTime, b.replacementTime),
        render: ({ replacementTime }) => (
          <span className="custom-table--column">{replacementTime}</span>
        ),
      },
      {
        key: 'totalRevenueExposure',
        title: 'Total Revenue Exposure',
        width: 100,
        align: 'right',
        sorter: canViewFinancials
          ? (a, b) =>
              tableSortingMethods.number(
                a.strategy?.grossRevenueExposure,
                b.strategy?.grossRevenueExposure,
              )
          : undefined,
        render: ({ strategy }) => (
          <span className="custom-table--column">
            <FinancialData text={strategy?.grossRevenueExposure} />
          </span>
        ),
      },
      {
        key: 'mitigation',
        title: 'Mitigation',
        width: 100,
        align: 'right',
        sorter: canViewFinancials
          ? (a, b) =>
              tableSortingMethods.number(
                a.strategy?.mitigation,
                b.strategy?.mitigation,
              )
          : undefined,
        render: ({ strategy }) => (
          <span className="custom-table--column">
            {<FinancialData text={strategy?.mitigation} />}
          </span>
        ),
      },
      {
        key: 'residualRevenueExposure',
        title: 'Residual Revenue Exposure',
        width: 100,
        align: 'right',
        sorter: canViewFinancials
          ? (a, b) =>
              tableSortingMethods.number(
                a.strategy?.netRevenueExposure,
                b.strategy?.netRevenueExposure,
              )
          : undefined,
        render: ({ strategy }) => (
          <span className="custom-table--column">
            {<FinancialData text={strategy?.netRevenueExposure} />}
          </span>
        ),
      },
      {
        key: 'strategyAge',
        title: 'Strategy Age',
        width: 100,
        align: 'right',
        sorter: (a, b) =>
          tableSortingMethods.number(
            a.strategy.strategyAge,
            b.strategy?.strategyAge,
          ),
        render: ({ strategy }) =>
          strategy?.strategyAge > 0 ? (
            <div className="custom-table--column">{strategy?.strategyAge}</div>
          ) : (
            <span className="placeholder-text text-sm">No available data</span>
          ),
      },
      {
        key: 'strategyStatus',
        title: 'Strategy Status',
        width: 150,
        align: 'center',
        sorter: (a, b) =>
          tableSortingMethods.string(
            a.strategy?.bcpStatus,
            b.strategy?.bcpStatus,
          ),
        render: ({ strategy: { bcpStatus } }) => (
          <span className="custom-table--column">
            {bcpStatus ? (
              `${getStatusEmoji(bcpStatus)}   ${bcpStatus}`
            ) : (
              <span className="placeholder-text">Not Started</span>
            )}
          </span>
        ),
      },
      {
        key: 'strategyRequired',
        title: 'Strategy Required',
        width: 100,
        align: 'center',
        sorter: (a, b) =>
          tableSortingMethods.boolean(
            a.strategy?.required,
            b.strategy?.required,
          ),
        render: ({ strategy }) => (
          <div className="custom-table--column">
            {renderStrategyRequired(strategy)}
          </div>
        ),
      },
    ],
    [data, onChangeStrategyRequired],
  );

  const extendActions = (record: any) => {
    return (
      record?.strategy?.required && (
        <div>
          <Button
            type="primary-rounded"
            className="text-sm min-w-[160px]"
            onClick={() => {
              navigate(
                `/process-areas-and-strategies/${locationId}/${record.key}`,
              );
            }}
          >
            Review Strategy
          </Button>
        </div>
      )
    );
  };

  return (
    <>
      <QueryErrorBoundary error={metaDataError}>
        <TitleUpdater title={`Process Areas & Process Strategies`} />
        <TitleBar
          title="BCP Hub"
          subtitle={`${locationName} // Process Areas & Process Strategies`}
        />
        <QueryErrorBoundary error={error}>
          <TableV3
            title="Process Area Strategy Inventory"
            columns={customColumns}
            data={tableData}
            loading={loadingStates}
            customStyles={'bcpprocessareas'}
            extendActions={extendActions}
            exportExcelColumns={baseColumns}
          />
        </QueryErrorBoundary>
      </QueryErrorBoundary>
      <Footer backPath={backPath} />
    </>
  );
};

export default BCPProcessAreas;
